import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const QuadraticFormulaCalculator = () => {
  const [inputData, setInputData] = useState({
    a: '',
    b: '',
    c: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateQuadratic = () => {
    const { a, b, c } = inputData;

    // Validate inputs
    if (!a || !b || !c) {
      setError('Please enter values for coefficients a, b, and c');
      return;
    }

    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    const cNum = parseFloat(c);

    if (isNaN(aNum) || isNaN(bNum) || isNaN(cNum)) {
      setError('Please enter valid numbers for coefficients');
      return;
    }

    if (aNum === 0) {
      setError('Coefficient a cannot be zero in a quadratic equation');
      return;
    }

    // Calculate discriminant
    const discriminant = Math.pow(bNum, 2) - 4 * aNum * cNum;

    let roots = [];
    let calculationSteps = [];

    // Calculate roots based on discriminant
    if (discriminant > 0) {
      // Two real roots
      const root1 = (-bNum + Math.sqrt(discriminant)) / (2 * aNum);
      const root2 = (-bNum - Math.sqrt(discriminant)) / (2 * aNum);
      roots = [root1, root2];

      calculationSteps = [
        `Step 1: Calculate discriminant: b² - 4ac = ${bNum}² - 4×${aNum}×${cNum} = ${discriminant}`,
        `Step 2: Since discriminant > 0, there are two real roots`,
        `Step 3: Root 1 = (-b + √D)/(2a) = (-${bNum} + √${discriminant})/(2×${aNum}) = ${root1.toFixed(4)}`,
        `Step 4: Root 2 = (-b - √D)/(2a) = (-${bNum} - √${discriminant})/(2×${aNum}) = ${root2.toFixed(4)}`
      ];
    } else if (discriminant === 0) {
      // One real root (double root)
      const root = -bNum / (2 * aNum);
      roots = [root];

      calculationSteps = [
        `Step 1: Calculate discriminant: b² - 4ac = ${bNum}² - 4×${aNum}×${cNum} = ${discriminant}`,
        `Step 2: Since discriminant = 0, there is one real root (double root)`,
        `Step 3: Root = -b/(2a) = -${bNum}/(2×${aNum}) = ${root.toFixed(4)}`
      ];
    } else {
      // Complex roots
      const realPart = -bNum / (2 * aNum);
      const imaginaryPart = Math.sqrt(-discriminant) / (2 * aNum);
      roots = [
        `${realPart.toFixed(4)} + ${imaginaryPart.toFixed(4)}i`,
        `${realPart.toFixed(4)} - ${imaginaryPart.toFixed(4)}i`
      ];

      calculationSteps = [
        `Step 1: Calculate discriminant: b² - 4ac = ${bNum}² - 4×${aNum}×${cNum} = ${discriminant}`,
        `Step 2: Since discriminant < 0, there are two complex roots`,
        `Step 3: Real part = -b/(2a) = -${bNum}/(2×${aNum}) = ${realPart.toFixed(4)}`,
        `Step 4: Imaginary part = √|D|/(2a) = √${-discriminant}/(2×${aNum}) = ${imaginaryPart.toFixed(4)}`,
        `Step 5: Complex roots: ${roots[0]} and ${roots[1]}`
      ];
    }

    // Create visualization data
    const chartData = {
      labels: ['Root 1', 'Root 2'],
      datasets: [{
        label: 'Root Values',
        data: roots.map(root => {
          if (typeof root === 'number') return root;
          // For complex roots, show magnitude
          const [real, imag] = root.split(' ± ').map(part => parseFloat(part));
          return Math.sqrt(real * real + imag * imag);
        }),
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }]
    };

    setResults({
      a: aNum,
      b: bNum,
      c: cNum,
      discriminant,
      roots,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    a: 1,
    b: -5,
    c: 6,
    roots: [3, 2]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-teal-100 to-cyan-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Quadratic Formula Calculator | Solve Quadratic Equations</title>
          <meta name="description" content="Solve quadratic equations using the quadratic formula. This calculator finds roots of quadratic equations in the form ax² + bx + c = 0, including real and complex solutions." />
          <meta name="keywords" content="quadratic formula, quadratic equation solver, solve quadratic equation, quadratic calculator, math calculator, algebra calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-teal-600 mr-4">🧮</div>
            <h1 className="text-3xl font-bold text-gray-800">Quadratic Formula Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Solve quadratic equations using the quadratic formula. This calculator finds roots of quadratic equations in the form <strong>ax² + bx + c = 0</strong>, including real and complex solutions.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Coefficient a:</label>
              <input
                type="number"
                name="a"
                value={inputData.a}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter coefficient a"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Coefficient b:</label>
              <input
                type="number"
                name="b"
                value={inputData.b}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter coefficient b"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Coefficient c:</label>
              <input
                type="number"
                name="c"
                value={inputData.c}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter coefficient c"
              />
            </div>
          </div>

          <button
            onClick={calculateQuadratic}
            className="w-full py-3 px-4 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Solve Quadratic Equation
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Quadratic Equation</h3>
                  <p className="text-xl font-bold mb-4">
                    {results.a}x² + {results.b}x + {results.c} = 0
                  </p>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Discriminant</h3>
                  <p className="text-lg">
                    D = {results.discriminant}
                  </p>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Roots</h3>
                  <ul className="list-disc pl-6 mt-2">
                    {results.roots.map((root, index) => (
                      <li key={index} className="text-lg">{`Root ${index + 1}: ${root}`}</li>
                    ))}
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Equation Visualization</h3>
                  <div className="h-48 bg-white rounded-lg flex items-center justify-center">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <path 
                        d="M20,100 Q100,20 180,100 T20,100" 
                        stroke="teal" 
                        strokeWidth="2" 
                        fill="none"
                        transform={`scale(1, -1) translate(0, -200)`}
                      />
                      <line x1="0" y1="100" x2="200" y2="100" stroke="gray" strokeWidth="1" />
                      <line x1="100" y1="0" x2="100" y2="200" stroke="gray" strokeWidth="1" />
                    </svg>
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Root Values'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Roots'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Root Magnitudes'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-teal-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the quadratic equation x² - 5x + 6 = 0:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Equation: x² - 5x + 6 = 0</li>
                <li>Coefficients: a = {exampleData.a}, b = {exampleData.b}, c = {exampleData.c}</li>
                <li>Discriminant: 25 - 24 = 1</li>
                <li>Roots: {exampleData.roots[0]}, {exampleData.roots[1]}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate discriminant: D = (-5)² - 4×1×6 = 1</li>
                <li>Since D greater than 0, there are two real roots</li>
                <li>Root 1: (5 + √1)/2 = 3</li>
                <li>Root 2: (5 - √1)/2 = 2</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-teal-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Quadratic Formula Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Coefficients:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the coefficients a, b, and c for the equation ax² + bx + c = 0</li>
                  <li>Ensure a ≠ 0 (otherwise it's not a quadratic equation)</li>
                </ul>
              </li>
              <li><strong>Click Solve:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The discriminant value</li>
                  <li>The type of roots (real or complex)</li>
                  <li>The actual roots of the equation</li>
                  <li>A visual representation of the roots</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-teal-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>The discriminant determines the nature of the roots</li>
                <li>D greater than 0: Two distinct real roots</li>
                <li>D = 0: One real root (double root)</li>
                <li>D less than 0: Two complex conjugate roots</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Quadratic Equations <span className="text-teal-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Quadratic equations are second-degree polynomial equations of the form:
            </p>
            <p className="font-bold text-xl mt-4">ax² + bx + c = 0</p>
            <p className="mt-2">
              Where:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>a</strong>, <strong>b</strong>, and <strong>c</strong> are coefficients</li>
                <li><strong>a ≠ 0</strong> (otherwise it's a linear equation)</li>
                <li>The solutions are called roots</li>
              </ul>
            </p>
            <p className="mt-4">
              The quadratic formula is used to find the roots:
            </p>
            <p className="font-bold text-xl mt-2">x = (-b ± √(b² - 4ac)) / 2a</p>
            <p className="mt-4">
              This formula has numerous applications in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Physics:</strong> Projectile motion calculations</li>
                <li><strong>Engineering:</strong> System design and analysis</li>
                <li><strong>Economics:</strong> Cost and revenue optimization</li>
                <li><strong>Computer Graphics:</strong> Rendering curves and surfaces</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-teal-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Check for Real Solutions:</strong> Use discriminant to determine solution type</li>
              <li><strong>Verify Solutions:</strong> Substitute roots back into the original equation</li>
              <li><strong>Factor When Possible:</strong> Factoring can be faster for simple quadratics</li>
              <li><strong>Use Complex Numbers:</strong> For equations with no real solutions</li>
            </ul>
            <p className="italic mt-4">
              Note: Quadratic equations can have 0, 1, or 2 real solutions depending on the discriminant value.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default QuadraticFormulaCalculator;